// Phase I-A.5 — Frontend client for the single-source Project Schema API.
//
// Consumers:
//   • Public surfaces (ProjectsPage / ProjectFilterPills / ProjectDetail
//     / future public Project form) → use `fetchProjectSchemaPublic()`.
//   • Admin Schema Builder (Phase I-B target) → use the `admin*` helpers.
//
// IMPORTANT — Phase I-A.6 PARITY RULE:
//   • Backend `/api/project-schema/public` is the canonical source of
//     option labels + keys.
//   • Frontend `/lib/projectAdapters.js` may ALIAS legacy raw values
//     into canonical keys (for read compatibility) but MUST agree on
//     the same canonical set. Parity tests live in
//     `/app/backend/tests/test_phase_1a_foundation.py`.
//
// Do NOT add new hard-coded option arrays here. The Phase I-B
// migration of hard-coded arrays (BHK_OPTIONS, STATUS_OPTIONS, …) will
// switch to fetching from this client.

import api from "./api";

let _publicCache = null;
let _publicCachePromise = null;

/**
 * Fetch the sanitized public schema. Cached process-wide (single-page-app
 * pattern) — callers that need fresh data can pass `{ force: true }`.
 */
export async function fetchProjectSchemaPublic({ force = false } = {}) {
  if (_publicCache && !force) return _publicCache;
  if (_publicCachePromise && !force) return _publicCachePromise;
  _publicCachePromise = api
    .get("/project-schema/public")
    .then((r) => {
      _publicCache = r.data;
      _publicCachePromise = null;
      return _publicCache;
    })
    .catch((err) => {
      _publicCachePromise = null;
      throw err;
    });
  return _publicCachePromise;
}

/** Convenience: return only the catalog options for a scope. */
export async function fetchProjectCatalog(scope, { force = false } = {}) {
  const data = await fetchProjectSchemaPublic({ force });
  return (data.catalog_options || []).filter((o) => o.scope === scope);
}

/** Convenience: return only the field definitions, sorted. */
export async function fetchProjectFields({ force = false } = {}) {
  const data = await fetchProjectSchemaPublic({ force });
  return [...(data.fields || [])].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
}

// ────────────────── Admin helpers (Phase I-B will wire these into UI) ──────────────────

export async function fetchProjectSchemaAdmin() {
  const r = await api.get("/admin/project-schema");
  return r.data;
}

export async function adminPatchProjectField(key, patch) {
  const r = await api.patch(`/admin/project-schema/fields/${encodeURIComponent(key)}`, patch);
  _publicCache = null; // invalidate
  return r.data;
}

export async function adminCreateProjectCatalogOption({ scope, label, sort_order }) {
  const r = await api.post("/admin/project-schema/catalog", { scope, label, sort_order });
  _publicCache = null;
  return r.data;
}

export async function adminPatchProjectCatalogOption(opt_id, patch) {
  const r = await api.patch(`/admin/project-schema/catalog/${encodeURIComponent(opt_id)}`, patch);
  _publicCache = null;
  return r.data;
}

export async function adminArchiveProjectCatalogOption(opt_id) {
  const r = await api.delete(`/admin/project-schema/catalog/${encodeURIComponent(opt_id)}`);
  _publicCache = null;
  return r.data;
}

/** Test helper: clear the cache between tests / forced refreshes. */
export function _resetProjectSchemaCache() {
  _publicCache = null;
  _publicCachePromise = null;
}

// ────────────────── React hooks ──────────────────

/**
 * Phase I-B.1 corrective gate — A tiny shared hook that fetches the
 * public project schema and returns a status-label resolver. Used by
 * every component that needs to render a project status label
 * (ProjectDetail, AdminProjectsTab, BuilderOverview, ProjectsPage,
 * BuilderProjectsTab) so the labels are NEVER hard-coded.
 *
 * Returns `{ statusLabel, statusOptions, ready }` — `statusLabel(key)`
 * falls back to the raw key when the schema is unreachable AND the
 * raw key isn't present (so the UI never goes blank).
 */
import { useEffect, useState } from "react";
export function useProjectStatusLabels() {
  const [opts, setOpts] = useState([]);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    fetchProjectSchemaPublic()
      .then((d) => {
        if (cancelled) return;
        const list = (d.catalog_options || [])
          .filter((o) => o.scope === "project_status")
          .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
          .map((o) => ({ key: o.key, label: o.label }));
        setOpts(list);
        setReady(true);
      })
      .catch(() => { if (!cancelled) setReady(true); });
    return () => { cancelled = true; };
  }, []);
  const map = {};
  for (const o of opts) map[o.key] = o.label;
  return {
    statusOptions: opts,
    statusLabel: (key) => map[key] || (key ? String(key).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "—"),
    ready,
  };
}
